import React, { useState } from 'react';
import { CourtConfig, SportType, CourtType } from '../../types';
import { useModal } from '../../context/ModalContext';
import CourtCard from './CourtCard';

// Sport configurations matching CourtCard
const SPORT_OPTIONS: Array<{
  value: SportType;
  label: string;
  gradient: string;
  description: string;
  icon: JSX.Element;
}> = [
  {
    value: 'pickleball',
    label: 'Pickleball',
    gradient: 'linear-gradient(135deg, #236332 0%, #3d9951 100%)',
    description: 'The fastest growing sport in America',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
      </svg>
    ),
  },
  {
    value: 'padel',
    label: 'Padel',
    gradient: 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)',
    description: 'Fast-growing global racket sport',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="9" />
        <path d="M7 7c3 1 7 1 10 0M7 17c3-1 7-1 10 0" />
      </svg>
    ),
  },
  {
    value: 'tennis',
    label: 'Tennis',
    gradient: 'linear-gradient(135deg, #2f58dd 0%, #60a5fa 100%)',
    description: 'Classic racquet sport',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="9" />
        <path d="M5.5 5.5c3.5 3.5 9.5 3.5 13 0M5.5 18.5c3.5-3.5 9.5-3.5 13 0" />
      </svg>
    ),
  },
  {
    value: 'basketball',
    label: 'Basketball',
    gradient: 'linear-gradient(135deg, #dd7d47 0%, #f59e0b 100%)',
    description: 'Indoor & outdoor hoops',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18M3 12c0-2.5 4-4.5 9-4.5s9 2 9 4.5M3 12c0 2.5 4 4.5 9 4.5s9-2 9-4.5" />
      </svg>
    ),
  },
  {
    value: 'volleyball',
    label: 'Volleyball',
    gradient: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
    description: 'Beach or indoor courts',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3c0 3 2 5.5 5 7M12 3c0 3-2 5.5-5 7M12 21c0-5-4-8-9-9M12 21c0-5 4-8 9-9" />
      </svg>
    ),
  },
];

const COURT_TYPE_OPTIONS: Array<{
  value: CourtType;
  label: string;
  description: string;
  icon: JSX.Element;
}> = [
  {
    value: 'indoor',
    label: 'Indoor',
    description: 'Climate controlled facility',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    value: 'outdoor',
    label: 'Outdoor',
    description: 'Open air environment',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="5" />
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
      </svg>
    ),
  },
  {
    value: 'covered',
    label: 'Covered',
    description: 'Semi-outdoor with roof',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 7V4h16v3M9 20h6M12 4v16" />
        <path d="M2 7h20M4 7l2 13h12l2-13" />
      </svg>
    ),
  },
];

const AMENITIES_OPTIONS: Array<{ name: string; icon: string }> = [
  { name: 'Climate controlled', icon: '❄️' },
  { name: 'Professional lighting', icon: '💡' },
  { name: 'Viewing area', icon: '👀' },
  { name: 'Equipment rental', icon: '🎾' },
  { name: 'Locker rooms', icon: '🚿' },
  { name: 'Showers', icon: '🚿' },
  { name: 'Water fountains', icon: '💧' },
  { name: 'WiFi', icon: '📶' },
  { name: 'Pro shop', icon: '🛒' },
  { name: 'Parking', icon: '🅿️' },
  { name: 'Wheelchair accessible', icon: '♿' },
  { name: 'Seating area', icon: '🪑' },
];

const INTEGRATION_LABELS: Record<string, string> = {
  courtreserve: 'CourtReserve',
  playbypoint: 'PlayByPoint',
  'google-calendar': 'Google Calendar',
};

interface CourtsAndAvailabilityProps {
  courts: CourtConfig[];
  operatingHours?: {
    dayOfWeek: number;
    openTime: string;
    closeTime: string;
    isClosed: boolean;
  }[];
  onAddCourt: (court: CourtConfig) => Promise<void>;
  onUpdateCourt: (id: string, updates: Partial<CourtConfig>) => Promise<void>;
  onDeleteCourt: (id: string) => Promise<void>;
  selectedCourtId?: string | null;
  onSelectCourt?: (id: string) => void;
}

export default function CourtsAndAvailability({
  courts,
  onAddCourt,
  onUpdateCourt,
  onDeleteCourt,
  selectedCourtId,
  onSelectCourt,
}: CourtsAndAvailabilityProps) {
  const { showAlert, showDanger } = useModal();

  // Court form state
  const [showCourtForm, setShowCourtForm] = useState(false);
  const [editingCourt, setEditingCourt] = useState<CourtConfig | null>(null);
  const [courtFormData, setCourtFormData] = useState<Partial<CourtConfig>>({
    name: '',
    sportType: 'pickleball',
    courtType: 'indoor',
    description: '',
    amenities: [],
    isActive: true,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Court form handlers
  const resetCourtForm = () => {
    setCourtFormData({
      name: '',
      sportType: 'pickleball',
      courtType: 'indoor',
      description: '',
      amenities: [],
      isActive: true,
    });
    setEditingCourt(null);
    setShowCourtForm(false);
  };

  const handleCourtSubmit = async () => {
    if (!courtFormData.name) {
      await showAlert('Missing Name', 'Please enter a court name.', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      if (editingCourt) {
        await onUpdateCourt(editingCourt.id, courtFormData);
      } else {
        const newCourt: CourtConfig = {
          id: `court-${Date.now()}`,
          name: courtFormData.name || '',
          sportType: courtFormData.sportType as SportType,
          courtType: courtFormData.courtType as CourtType,
          description: courtFormData.description || '',
          amenities: courtFormData.amenities || [],
          images: [],
          isActive: courtFormData.isActive ?? true,
        };
        await onAddCourt(newCourt);
      }
      resetCourtForm();
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to save court', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCourt = (court: CourtConfig) => {
    setCourtFormData({
      name: court.name,
      sportType: court.sportType,
      courtType: court.courtType,
      description: court.description,
      amenities: court.amenities,
      isActive: court.isActive,
    });
    setEditingCourt(court);
    setShowCourtForm(true);
  };

  const handleDeleteCourt = async (courtId: string) => {
    const court = courts.find(c => c.id === courtId);
    const confirmed = await showDanger(
      'Delete Court',
      `Are you sure you want to delete "${court?.name || 'this court'}"? All availability settings for this court will be permanently removed.`,
      'Delete'
    );

    if (confirmed) {
      try {
        await onDeleteCourt(courtId);
      } catch (err) {
        await showAlert('Error', err instanceof Error ? err.message : 'Failed to delete court', 'error');
      }
    }
  };

  const toggleAmenity = (amenity: string) => {
    const current = courtFormData.amenities || [];
    if (current.includes(amenity)) {
      setCourtFormData({ ...courtFormData, amenities: current.filter((a) => a !== amenity) });
    } else {
      setCourtFormData({ ...courtFormData, amenities: [...current, amenity] });
    }
  };

  const selectedSportConfig = SPORT_OPTIONS.find(s => s.value === courtFormData.sportType);
  const mappedExternalName = editingCourt?.externalMappedName?.trim() || 'Not mapped';
  const mappedIntegrationName = editingCourt?.syncedIntegrationType
    ? (INTEGRATION_LABELS[editingCourt.syncedIntegrationType] || editingCourt.syncedIntegrationType)
    : null;

  return (
    <div className="courts-and-availability">
      {/* Courts Section */}
      <div className="courts-section-v2">
        <div className="courts-section-v2__header">
          <div className="courts-section-v2__title-area">
            <h3 className="courts-section-v2__title">Your Courts</h3>
            <p className="courts-section-v2__subtitle">
              {courts.length === 0
                ? 'Add your first court to get started'
                : `${courts.length} court${courts.length !== 1 ? 's' : ''} configured`}
            </p>
          </div>
          <button
            className="courts-section-v2__add-btn"
            onClick={() => {
              resetCourtForm();
              setShowCourtForm(true);
            }}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            <span>Add Court</span>
          </button>
        </div>

        {courts.length === 0 ? (
          <div className="courts-empty-v2">
            <div className="courts-empty-v2__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 12h18M12 3v18" />
              </svg>
            </div>
            <h4 className="courts-empty-v2__title">No courts yet</h4>
            <p className="courts-empty-v2__description">
              Create your first court to start managing availability and accepting bookings
            </p>
            <button
              className="courts-empty-v2__cta"
              onClick={() => {
                resetCourtForm();
                setShowCourtForm(true);
              }}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Create Your First Court
            </button>
          </div>
        ) : (
          <div className="courts-grid-v2">
            {[...courts].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })).map((court) => (
              <CourtCard
                key={court.id}
                court={court}
                isSelected={selectedCourtId === court.id}
                onClick={() => onSelectCourt?.(court.id)}
                onEdit={() => handleEditCourt(court)}
                onDelete={() => handleDeleteCourt(court.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Court Form Modal */}
      {showCourtForm && (
        <div className="court-modal-overlay" onClick={resetCourtForm}>
          <div className="court-modal" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="court-modal__header">
              <div className="court-modal__header-content">
                <h2 className="court-modal__title">
                  {editingCourt ? 'Edit Court' : 'Add New Court'}
                </h2>
                <p className="court-modal__subtitle">
                  {editingCourt
                    ? 'Update your court details and settings'
                    : 'Configure your court for reservations'}
                </p>
              </div>
              <button className="court-modal__close" onClick={resetCourtForm}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="court-modal__body">
              {/* Court Name */}
              <div className="court-form-section">
                <label className="court-form-label">
                  Court Name
                  <span className="court-form-label__required">*</span>
                </label>
                <input
                  type="text"
                  className="court-form-input court-form-input--name"
                  value={courtFormData.name}
                  onChange={(e) => setCourtFormData({ ...courtFormData, name: e.target.value })}
                  placeholder="e.g., Court 1, Main Arena, Center Court"
                  autoFocus
                />
              </div>

              <div className="court-form-section">
                <label className="court-form-label">External Mapped Name</label>
                <div className="court-form-locked">
                  <div className="court-form-locked__value">{mappedExternalName}</div>
                  <div className="court-form-locked__meta">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12" aria-hidden="true">
                      <path fillRule="evenodd" d="M5 8V6a5 5 0 1110 0v2h1a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2v-6a2 2 0 012-2h1zm2 0h6V6a3 3 0 10-6 0v2z" clipRule="evenodd" />
                    </svg>
                    <span>
                      {mappedIntegrationName
                        ? `Synced from ${mappedIntegrationName}`
                        : 'Locked field. This court is not synced to an external system.'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sport Type Selector */}
              <div className="court-form-section">
                <label className="court-form-label">Sport Type</label>
                <div className="sport-selector">
                  {SPORT_OPTIONS.map((sport) => (
                    <button
                      key={sport.value}
                      type="button"
                      className={`sport-selector__option ${courtFormData.sportType === sport.value ? 'sport-selector__option--selected' : ''}`}
                      onClick={() => setCourtFormData({ ...courtFormData, sportType: sport.value })}
                      style={{
                        '--sport-gradient': sport.gradient,
                      } as React.CSSProperties}
                    >
                      <div className="sport-selector__icon">{sport.icon}</div>
                      <div className="sport-selector__content">
                        <span className="sport-selector__label">{sport.label}</span>
                        <span className="sport-selector__description">{sport.description}</span>
                      </div>
                      {courtFormData.sportType === sport.value && (
                        <div className="sport-selector__check">
                          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Court Type Selector */}
              <div className="court-form-section">
                <label className="court-form-label">Court Environment</label>
                <div className="court-type-selector">
                  {COURT_TYPE_OPTIONS.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`court-type-selector__option ${courtFormData.courtType === type.value ? 'court-type-selector__option--selected' : ''}`}
                      onClick={() => setCourtFormData({ ...courtFormData, courtType: type.value })}
                    >
                      <div className="court-type-selector__icon">{type.icon}</div>
                      <span className="court-type-selector__label">{type.label}</span>
                      <span className="court-type-selector__description">{type.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="court-form-section">
                <label className="court-form-label">
                  Description
                  <span className="court-form-label__optional">(optional)</span>
                </label>
                <textarea
                  className="court-form-textarea"
                  value={courtFormData.description}
                  onChange={(e) => setCourtFormData({ ...courtFormData, description: e.target.value })}
                  placeholder="Describe surface type, special features, or notable equipment..."
                  rows={3}
                />
              </div>

              {/* Amenities */}
              <div className="court-form-section">
                <label className="court-form-label">
                  Amenities
                  <span className="court-form-label__count">
                    {courtFormData.amenities?.length || 0} selected
                  </span>
                </label>
                <div className="amenities-selector">
                  {AMENITIES_OPTIONS.map((amenity) => {
                    const isSelected = courtFormData.amenities?.includes(amenity.name);
                    return (
                      <button
                        key={amenity.name}
                        type="button"
                        className={`amenity-chip ${isSelected ? 'amenity-chip--selected' : ''}`}
                        onClick={() => toggleAmenity(amenity.name)}
                      >
                        <span className="amenity-chip__icon">{amenity.icon}</span>
                        <span className="amenity-chip__label">{amenity.name}</span>
                        {isSelected && (
                          <svg className="amenity-chip__check" viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Active Status */}
              <div className="court-form-section court-form-section--toggle">
                <div className="court-status-toggle">
                  <div className="court-status-toggle__content">
                    <span className="court-status-toggle__label">Court Status</span>
                    <span className="court-status-toggle__description">
                      {courtFormData.isActive
                        ? 'This court is available for bookings'
                        : 'This court is hidden from customers'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`court-status-toggle__switch ${courtFormData.isActive ? 'court-status-toggle__switch--active' : ''}`}
                    onClick={() => setCourtFormData({ ...courtFormData, isActive: !courtFormData.isActive })}
                  >
                    <span className="court-status-toggle__knob" />
                    <span className="court-status-toggle__text">
                      {courtFormData.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="court-modal__footer">
              <button
                type="button"
                className="court-modal__btn court-modal__btn--secondary"
                onClick={resetCourtForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="court-modal__btn court-modal__btn--primary"
                onClick={handleCourtSubmit}
                disabled={isSaving || !courtFormData.name}
                style={{
                  background: selectedSportConfig?.gradient || 'var(--primary)',
                }}
              >
                {isSaving ? (
                  <>
                    <span className="court-modal__btn-spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {editingCourt ? 'Save Changes' : 'Create Court'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Section */}
    </div>
  );
}
